/**
 * BJCP 2021 beer guidelines — categories 23–34.
 *
 * The vitals are facts and are reproduced as published: original and final
 * gravity, bitterness, colour and alcohol define the numeric envelope a judge
 * measures against, and a style bar drawn from anything else would be wrong.
 * The `overallImpression` prose, by contrast, is OURS — written from scratch,
 * not BJCP text and not a paraphrase of it.
 *
 * Categories 28–34 are specialty and "base style + X" families. Their vitals
 * legitimately carry `null` because they vary with whatever base style the
 * entrant declares; a fabricated range there would be drawn as a style bar and
 * read as fact, so `null` is the honest value.
 */

import type { Style } from '../../schema/style';

/** BJCP 2021 sub-styles, categories 23–34. @see Style */
export const styleCategories23To34: readonly Style[] = [
  // -------------------------------------------------------------------------
  // 23 — European Sour Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-23a',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'A',
    name: 'Berliner Weisse',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.028, max: 1.032 },
      fg: { min: 1.003, max: 1.006 },
      ibu: { min: 3, max: 8 },
      srm: { min: 2, max: 3 },
      abv: { min: 2.8, max: 3.8 },
    },
    overallImpression:
      'A tiny, pale wheat beer built around clean lactic sourness and almost no hop presence. It is spritzy, dry and pointedly refreshing, with bread-dough wheat underneath and sometimes a faint barnyard edge from Brettanomyces. Low alcohol keeps it drinkable by the liter.',
    commercialExamples: [
      'Bayerischer Bahnhof Berliner Style Weisse',
      'Berliner Kindl Weisse',
      'The Bruery Hottenroth',
      'Freigeist Abraxxxas',
    ],
    tags: ['sour', 'wheat', 'german', 'pale', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-23b',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'B',
    name: 'Flanders Red Ale',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.048, max: 1.057 },
      fg: { min: 1.002, max: 1.012 },
      ibu: { min: 10, max: 25 },
      srm: { min: 10, max: 16 },
      abv: { min: 4.6, max: 6.5 },
    },
    overallImpression:
      'Deep reddish-brown and strikingly wine-like, with dark cherry and plum fruit riding on a sharp acetic-plus-lactic tang. Long maturation in oak foeders builds vanilla and tannin, and most examples blend young and aged beer to land the balance. Hops are a rounding error here.',
    commercialExamples: [
      'Rodenbach Grand Cru',
      'Rodenbach Klassiek',
      'Duchesse de Bourgogne',
      'Verhaeghe Vichtenaar',
      'New Belgium La Folie',
    ],
    tags: ['sour', 'belgian', 'oak-aged', 'red', 'blended', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-23c',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'C',
    name: 'Oud Bruin',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.04, max: 1.074 },
      fg: { min: 1.008, max: 1.012 },
      ibu: { min: 20, max: 25 },
      srm: { min: 15, max: 22 },
      abv: { min: 4.0, max: 8.0 },
    },
    overallImpression:
      'The maltier, darker cousin of the Flanders red: caramel, raisin and toast carry a softer, mostly lactic sourness. Aging happens in tanks rather than oak, so there is no wood character and the fruit leans towards dried plum and fig. Fuller-bodied and rounder than its red sibling.',
    commercialExamples: [
      'Liefmans Goudenband',
      'Liefmans Oud Bruin',
      'Petrus Oud Bruin',
      'Ichtegem Oud Bruin',
    ],
    tags: ['sour', 'belgian', 'brown', 'malty', 'aged', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-23d',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'D',
    name: 'Lambic',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.04, max: 1.054 },
      fg: { min: 1.001, max: 1.01 },
      ibu: { min: 0, max: 10 },
      srm: { min: 3, max: 7 },
      abv: { min: 5.0, max: 6.5 },
    },
    overallImpression:
      'Unblended spontaneously fermented wheat beer, usually served young and lightly carbonated straight from the cask. Expect a bracing lactic bite, dry finish and a hay-and-cellar funk from wild yeast and bacteria, with aged hops contributing aroma but essentially no bitterness.',
    commercialExamples: [
      'Cantillon Grand Cru Bruocsella',
      'Girardin Lambic',
      'De Cam Oude Lambiek',
      'Boon Lambiek',
    ],
    tags: ['sour', 'belgian', 'spontaneous', 'wheat', 'funky', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-23e',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'E',
    name: 'Gueuze',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.04, max: 1.06 },
      fg: { min: 1.0, max: 1.006 },
      ibu: { min: 0, max: 10 },
      srm: { min: 3, max: 7 },
      abv: { min: 5.0, max: 8.0 },
    },
    overallImpression:
      'A blend of young and old lambic refermented in the bottle, giving champagne-like carbonation over a very dry, very sour base. The blender is the author here: the goal is complexity and balance between fresh acidity and mature, leathery funk. Should be effervescent, not flabby.',
    commercialExamples: [
      'Boon Oude Geuze',
      'Cantillon Gueuze',
      'Drie Fonteinen Oude Geuze',
      'Lindemans Cuvee Rene',
      'Hanssens Oude Gueuze',
    ],
    tags: ['sour', 'belgian', 'spontaneous', 'blended', 'gueuze', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-23f',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'F',
    name: 'Fruit Lambic',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.04, max: 1.06 },
      fg: { min: 1.0, max: 1.01 },
      ibu: { min: 0, max: 10 },
      srm: { min: 3, max: 7 },
      abv: { min: 5.0, max: 8.5 },
    },
    overallImpression:
      'Lambic refermented on whole fruit — traditionally sour cherry or raspberry — until the sugar is gone and only fruit character and acidity remain. The best versions are dry and tart with the fruit woven into the wild-yeast funk rather than sitting on top of it as sweetness.',
    commercialExamples: [
      'Cantillon Kriek',
      'Cantillon Fou Foune',
      'Boon Oude Kriek',
      'Drie Fonteinen Oude Kriek',
      'Lindemans Framboise',
    ],
    tags: ['sour', 'belgian', 'spontaneous', 'fruit', 'kriek', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-23g',
    guideline: 'bjcp-2021',
    categoryNumber: 23,
    categoryLetter: 'G',
    name: 'Gose',
    categoryName: 'European Sour Ale',
    vitals: {
      og: { min: 1.036, max: 1.056 },
      fg: { min: 1.006, max: 1.01 },
      ibu: { min: 5, max: 12 },
      srm: { min: 3, max: 4 },
      abv: { min: 4.2, max: 4.8 },
    },
    overallImpression:
      'A pale Leipzig wheat beer seasoned with salt and coriander over a moderate lactic tartness. The salt is a savory lift rather than a flavor you name, and the coriander adds a citrus-spice note; the whole thing finishes crisp, dry and thirst-quenching.',
    commercialExamples: [
      'Bayerischer Bahnhof Leipziger Gose',
      'Ritterguts Gose',
      'Westbrook Gose',
      'Anderson Valley Holy Gose',
    ],
    tags: ['sour', 'german', 'wheat', 'salty', 'coriander', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 24 — Belgian Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-24a',
    guideline: 'bjcp-2021',
    categoryNumber: 24,
    categoryLetter: 'A',
    name: 'Witbier',
    categoryName: 'Belgian Ale',
    vitals: {
      og: { min: 1.044, max: 1.052 },
      fg: { min: 1.008, max: 1.012 },
      ibu: { min: 8, max: 20 },
      srm: { min: 2, max: 4 },
      abv: { min: 4.5, max: 5.5 },
    },
    overallImpression:
      'Hazy, straw-pale and softly creamy from a big charge of unmalted wheat, spiced lightly with coriander and bitter orange peel. Yeast supplies most of the aroma, and the finish is dry and quenching rather than sweet. Everything is understated: no single spice should announce itself.',
    commercialExamples: [
      'Hoegaarden Wit',
      'Allagash White',
      'St. Bernardus Wit',
      'Blanche de Bruxelles',
    ],
    tags: ['belgian', 'wheat', 'spiced', 'pale', 'hazy', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-24b',
    guideline: 'bjcp-2021',
    categoryNumber: 24,
    categoryLetter: 'B',
    name: 'Belgian Pale Ale',
    categoryName: 'Belgian Ale',
    vitals: {
      og: { min: 1.048, max: 1.054 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 20, max: 30 },
      srm: { min: 8, max: 14 },
      abv: { min: 4.8, max: 5.5 },
    },
    overallImpression:
      'An amber everyday ale from the Antwerp region: toasty, slightly biscuity malt with restrained fruity-spicy yeast and a gentle hop finish. Far tamer than the strong abbey styles, it is built for a second glass and shows a drier, more balanced profile than a British bitter.',
    commercialExamples: ['De Koninck', 'Palm Speciale', 'Ommegang Rare Vos', 'Brugse Zot Blond'],
    tags: ['belgian', 'amber', 'malty', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-24c',
    guideline: 'bjcp-2021',
    categoryNumber: 24,
    categoryLetter: 'C',
    name: 'Bière de Garde',
    categoryName: 'Belgian Ale',
    vitals: {
      og: { min: 1.06, max: 1.08 },
      fg: { min: 1.008, max: 1.016 },
      ibu: { min: 18, max: 28 },
      srm: { min: 6, max: 19 },
      abv: { min: 6.0, max: 8.5 },
    },
    overallImpression:
      'A strong, malt-forward farmhouse beer from French Flanders, made in blond, amber and brown versions and lagered cold before release. Toasty bread-crust malt leads, alcohol is warming but smooth, and the finish is cleaner and less spicy than a saison.',
    commercialExamples: [
      'Jenlain Ambree',
      'Saint Sylvestre 3 Monts',
      'Castelain Blond Biere de Garde',
      'Two Brothers Domaine DuPage',
    ],
    tags: ['french', 'farmhouse', 'malty', 'strong', 'biere-de-garde', 'ale'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 25 — Strong Belgian Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-25a',
    guideline: 'bjcp-2021',
    categoryNumber: 25,
    categoryLetter: 'A',
    name: 'Belgian Blond Ale',
    categoryName: 'Strong Belgian Ale',
    vitals: {
      og: { min: 1.062, max: 1.075 },
      fg: { min: 1.008, max: 1.018 },
      ibu: { min: 15, max: 30 },
      srm: { min: 4, max: 7 },
      abv: { min: 6.0, max: 7.5 },
    },
    overallImpression:
      'Golden, sweetish and approachable, with light honeyish malt, soft pear-and-pepper yeast character and a moderate alcohol warmth. It is the gentlest of the strong blond Belgians — rounder and less aggressive than a tripel, and nowhere near as dry as a golden strong ale.',
    commercialExamples: ['Leffe Blond', 'Affligem Blond', 'Grimbergen Blond', 'La Trappe Blond'],
    tags: ['belgian', 'blond', 'strong', 'malty', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-25b',
    guideline: 'bjcp-2021',
    categoryNumber: 25,
    categoryLetter: 'B',
    name: 'Saison',
    categoryName: 'Strong Belgian Ale',
    vitals: {
      og: { min: 1.048, max: 1.065 },
      fg: { min: 1.002, max: 1.008 },
      ibu: { min: 20, max: 35 },
      srm: { min: 5, max: 14 },
      abv: { min: 3.5, max: 9.5 },
    },
    overallImpression:
      'A bone-dry, highly carbonated farmhouse ale driven by peppery, citrusy yeast and a long bitter finish. Entries range from table strength to strong and from pale to dark, but every version should be attenuated, effervescent and refreshing rather than heavy.',
    commercialExamples: [
      'Saison Dupont',
      'Fantome Saison',
      'Boulevard Tank 7',
      'Ommegang Hennepin',
      'Saison de Pipaix',
    ],
    tags: ['belgian', 'farmhouse', 'saison', 'dry', 'spicy', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-25c',
    guideline: 'bjcp-2021',
    categoryNumber: 25,
    categoryLetter: 'C',
    name: 'Belgian Golden Strong Ale',
    categoryName: 'Strong Belgian Ale',
    vitals: {
      og: { min: 1.07, max: 1.095 },
      fg: { min: 1.005, max: 1.016 },
      ibu: { min: 22, max: 35 },
      srm: { min: 3, max: 6 },
      abv: { min: 7.5, max: 10.5 },
    },
    overallImpression:
      'Pale, deceptively strong and dangerously drinkable, with a huge rocky head and champagne-like carbonation. Sugar thins the body so the alcohol reads as warmth rather than weight, while the yeast throws pear, orange and white pepper over a crisp, dry finish.',
    commercialExamples: [
      'Duvel',
      'Delirium Tremens',
      'Brigand',
      'Lucifer',
      'North Coast Pranqster',
    ],
    tags: ['belgian', 'golden', 'strong', 'dry', 'ale'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 26 — Monastic Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-26a',
    guideline: 'bjcp-2021',
    categoryNumber: 26,
    categoryLetter: 'A',
    name: 'Belgian Single',
    categoryName: 'Monastic Ale',
    vitals: {
      og: { min: 1.044, max: 1.054 },
      fg: { min: 1.004, max: 1.01 },
      ibu: { min: 25, max: 45 },
      srm: { min: 3, max: 5 },
      abv: { min: 4.8, max: 6.0 },
    },
    overallImpression:
      'The table beer the monastery brews for itself: pale, dry, quite bitter and very high in carbonation. Grainy malt sits well back while spicy yeast and a firm hop bite drive the finish, making it the leanest and most bitter member of the abbey family.',
    commercialExamples: [
      'Westmalle Extra',
      'St. Bernardus Extra 4',
      'Achel 5 Blond',
      'Petrus Blond',
    ],
    tags: ['belgian', 'trappist', 'abbey', 'pale', 'dry', 'bitter', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-26b',
    guideline: 'bjcp-2021',
    categoryNumber: 26,
    categoryLetter: 'B',
    name: 'Belgian Dubbel',
    categoryName: 'Monastic Ale',
    vitals: {
      og: { min: 1.062, max: 1.075 },
      fg: { min: 1.008, max: 1.018 },
      ibu: { min: 15, max: 25 },
      srm: { min: 10, max: 17 },
      abv: { min: 6.0, max: 7.6 },
    },
    overallImpression:
      'Deep amber to brown, with raisin, plum and caramel from dark candi sugar rather than roasted grain. Yeast adds a spicy, faintly banana-like lift, and despite the rich flavor the finish stays dry with a soft alcohol warmth.',
    commercialExamples: [
      'Westmalle Dubbel',
      'Chimay Premiere',
      'St. Bernardus Pater 6',
      'Affligem Dubbel',
      'Ommegang Abbey Ale',
    ],
    tags: ['belgian', 'trappist', 'abbey', 'dubbel', 'dark', 'malty', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-26c',
    guideline: 'bjcp-2021',
    categoryNumber: 26,
    categoryLetter: 'C',
    name: 'Belgian Tripel',
    categoryName: 'Monastic Ale',
    vitals: {
      og: { min: 1.075, max: 1.085 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 20, max: 40 },
      srm: { min: 4.5, max: 7 },
      abv: { min: 7.5, max: 9.5 },
    },
    overallImpression:
      'Golden, strong and complex, balancing soft pilsner-malt sweetness against spicy phenols, orchard fruit esters and a substantial hop bitterness. Sugar drives high attenuation, so the beer drinks lighter than its gravity and finishes dry with lively carbonation.',
    commercialExamples: [
      'Westmalle Tripel',
      'Chimay Cinq Cents',
      'St. Bernardus Tripel',
      'Affligem Tripel',
      'Victory Golden Monkey',
    ],
    tags: ['belgian', 'trappist', 'abbey', 'tripel', 'golden', 'strong', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-26d',
    guideline: 'bjcp-2021',
    categoryNumber: 26,
    categoryLetter: 'D',
    name: 'Belgian Dark Strong Ale',
    categoryName: 'Monastic Ale',
    vitals: {
      og: { min: 1.075, max: 1.11 },
      fg: { min: 1.01, max: 1.024 },
      ibu: { min: 20, max: 35 },
      srm: { min: 12, max: 22 },
      abv: { min: 8.0, max: 12.0 },
    },
    overallImpression:
      'The biggest of the abbey beers: dark, rich and layered with dried fruit, toffee, vinous depth and a warming alcohol presence. Complexity comes from yeast and candi sugar rather than roast, and the best examples stay smooth and drinkable at strengths that should not be.',
    commercialExamples: [
      'Westvleteren 12',
      'Rochefort 10',
      'St. Bernardus Abt 12',
      'Chimay Grande Reserve',
      'Gouden Carolus Grand Cru',
    ],
    tags: ['belgian', 'trappist', 'abbey', 'quadrupel', 'dark', 'strong', 'ale'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 27 — Historical Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-27a',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'A',
    name: 'Kentucky Common',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.044, max: 1.055 },
      fg: { min: 1.01, max: 1.018 },
      ibu: { min: 15, max: 30 },
      srm: { min: 11, max: 20 },
      abv: { min: 4.0, max: 5.5 },
    },
    overallImpression:
      'A cheap, quick-turnaround dark ale once brewed around Louisville from a corn-heavy grist with a little caramel and black malt. It drinks like a light, grainy brown ale with a hint of coffee-ish roast, faint sourness in some interpretations, and a clean, easy finish.',
    commercialExamples: [],
    tags: ['historical', 'american', 'dark', 'corn', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-27b',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'B',
    name: 'Lichtenhainer',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.032, max: 1.04 },
      fg: { min: 1.004, max: 1.008 },
      ibu: { min: 5, max: 12 },
      srm: { min: 3, max: 6 },
      abv: { min: 3.5, max: 4.7 },
    },
    overallImpression:
      'A revived Thuringian wheat beer that pairs a light lactic tartness with a clear campfire smokiness from beechwood-smoked malt. It is low in alcohol, low in bitterness and highly carbonated — smoke and acidity share the stage without either taking over.',
    commercialExamples: ['Freigeist Lichtenhainer'],
    tags: ['historical', 'german', 'smoked', 'sour', 'wheat', 'sessionable', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-27c',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'C',
    name: 'London Brown Ale',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.033, max: 1.038 },
      fg: { min: 1.012, max: 1.015 },
      ibu: { min: 15, max: 20 },
      srm: { min: 22, max: 35 },
      abv: { min: 2.8, max: 3.6 },
    },
    overallImpression:
      'A very low-gravity, distinctly sweet dark brown ale from southern England, full of caramel and toffee with only a whisper of roast. The residual sweetness and low attenuation are the point; hops appear only to keep it from cloying.',
    commercialExamples: ["Mann's Original Brown Ale"],
    tags: ['historical', 'english', 'brown', 'sweet', 'sessionable', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-27d',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'D',
    name: 'Piwo Grodziskie',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.028, max: 1.032 },
      fg: { min: 1.006, max: 1.01 },
      ibu: { min: 20, max: 35 },
      srm: { min: 3, max: 6 },
      abv: { min: 2.5, max: 3.3 },
    },
    overallImpression:
      'A pale Polish wheat beer made entirely from oak-smoked wheat malt, sharply carbonated and noticeably bitter for its size. The smoke is clean and woody rather than meaty, and the beer finishes crisp and dry at well under 4% alcohol.',
    commercialExamples: ['Grodzisk Grodziskie', 'Professor Fritz Briem Grodziskie'],
    tags: ['historical', 'polish', 'smoked', 'wheat', 'sessionable', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-27e',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'E',
    name: 'Pre-Prohibition Lager',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.044, max: 1.06 },
      fg: { min: 1.01, max: 1.015 },
      ibu: { min: 25, max: 40 },
      srm: { min: 3, max: 6 },
      abv: { min: 4.5, max: 6.0 },
    },
    overallImpression:
      'The American pale lager as it was before 1920: more gravity, more hops and more grainy corn character than anything sold under that name today. Flaked maize dries the finish while a firm bitterness and real hop flavor keep it from tasting bland.',
    commercialExamples: ['Coors Batch 19', 'Schell Deer Brand'],
    tags: ['historical', 'american', 'lager', 'pale', 'corn', 'hoppy'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-27f',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'F',
    name: 'Pre-Prohibition Porter',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.046, max: 1.06 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 20, max: 30 },
      srm: { min: 18, max: 30 },
      abv: { min: 4.5, max: 6.0 },
    },
    overallImpression:
      'An American take on porter from the turn of the twentieth century, brewed with adjuncts alongside dark and caramel malts. Moderate roast and sweet chocolate notes sit over a light, adjunct-thinned body, and the hopping is noticeable without being assertive.',
    commercialExamples: [],
    tags: ['historical', 'american', 'porter', 'dark', 'roasty', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-27g',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'G',
    name: 'Roggenbier',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.046, max: 1.056 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 10, max: 20 },
      srm: { min: 14, max: 19 },
      abv: { min: 4.5, max: 6.0 },
    },
    overallImpression:
      'A dark Bavarian rye beer fermented with weizen yeast, so spicy grain character meets banana and clove. Rye gives it a distinctive earthy sharpness and a full, slick body, while bitterness stays low and the head is dense and lasting.',
    commercialExamples: [
      'Paulaner Roggen',
      'Thurn und Taxis Roggen',
      'Bayerischer Bahnhof Roggenbier',
    ],
    tags: ['historical', 'german', 'rye', 'dark', 'weizen-yeast', 'ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-27h',
    guideline: 'bjcp-2021',
    categoryNumber: 27,
    categoryLetter: 'H',
    name: 'Sahti',
    categoryName: 'Historical Beer',
    vitals: {
      og: { min: 1.076, max: 1.12 },
      fg: { min: 1.016, max: 1.02 },
      ibu: { min: 7, max: 15 },
      srm: { min: 4, max: 22 },
      abv: { min: 7.0, max: 11.0 },
    },
    overallImpression:
      'A strong Finnish farmhouse beer lautered through juniper branches and fermented with baking yeast, which throws enormous banana esters. It is unhopped or nearly so, uncarbonated, sweet and hazy, with resinous juniper running through a thick, bready malt base.',
    commercialExamples: ['Lammin Sahti', 'Finlandia Sahti', 'Hollolan Hirvi Sahti'],
    tags: ['historical', 'finnish', 'farmhouse', 'juniper', 'strong', 'ale'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 28 — American Wild Ale
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-28a',
    guideline: 'bjcp-2021',
    categoryNumber: 28,
    categoryLetter: 'A',
    name: 'Brett Beer',
    categoryName: 'American Wild Ale',
    vitals: {
      og: { min: 1.035, max: 1.07 },
      fg: { min: 0.995, max: 1.01 },
      ibu: { min: 5, max: 40 },
      srm: { min: 4, max: 20 },
      abv: { min: 3.5, max: 8.0 },
    },
    overallImpression:
      'A beer whose defining character comes from Brettanomyces rather than souring bacteria — tropical fruit, hay, leather or smoke depending on the strain, over a very dry finish. Acidity should be low; if the beer is properly sour it belongs elsewhere in this category.',
    commercialExamples: [
      'Boulevard Saison-Brett',
      'Victory Helios',
      'Russian River Sanctification',
    ],
    tags: ['wild', 'american', 'brettanomyces', 'funky', 'dry', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-28b',
    guideline: 'bjcp-2021',
    categoryNumber: 28,
    categoryLetter: 'B',
    name: 'Mixed-Fermentation Sour Beer',
    categoryName: 'American Wild Ale',
    vitals: {
      og: { min: 1.035, max: 1.07 },
      fg: { min: 1.0, max: 1.01 },
      ibu: { min: 5, max: 35 },
      srm: { min: 3, max: 25 },
      abv: { min: 3.5, max: 8.0 },
    },
    overallImpression:
      'Any base beer soured by a mixed culture of yeast and bacteria, often with long barrel maturation behind it. Judges look for balanced acidity married to funk and oak complexity, with the underlying beer still legible rather than buried under vinegar.',
    commercialExamples: [
      'Russian River Temptation',
      'Jolly Pumpkin Calabaza Blanca',
      'Cascade Vlad the Impaler',
      'Allagash Coolship Resurgam',
    ],
    tags: ['wild', 'american', 'sour', 'mixed-fermentation', 'barrel-aged', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-28c',
    guideline: 'bjcp-2021',
    categoryNumber: 28,
    categoryLetter: 'C',
    name: 'Wild Specialty Beer',
    categoryName: 'American Wild Ale',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A wild or sour beer carrying an added ingredient — fruit, spice, wood or sugar — which the entrant must declare along with the base beer. Vitals follow whatever base is declared, so judges assess how well the addition integrates with the acidity and funk instead of measuring it against a fixed range.',
    commercialExamples: [],
    tags: ['wild', 'american', 'sour', 'specialty', 'fruit', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-28d',
    guideline: 'bjcp-2021',
    categoryNumber: 28,
    categoryLetter: 'D',
    name: 'Straight Sour Beer',
    categoryName: 'American Wild Ale',
    vitals: {
      og: { min: 1.032, max: 1.045 },
      fg: { min: 1.004, max: 1.01 },
      ibu: { min: 2, max: 8 },
      srm: { min: 2, max: 17 },
      abv: { min: 3.2, max: 5.0 },
    },
    overallImpression:
      'Beer soured quickly and cleanly by Lactobacillus alone, with no Brettanomyces and no barrel funk anywhere in the profile. The result is bright, simple lactic acidity over a light malt base, finishing crisp and refreshing with almost no bitterness.',
    commercialExamples: [],
    tags: ['sour', 'american', 'kettle-sour', 'lactobacillus', 'clean', 'ale'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 29 — Fruit Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-29a',
    guideline: 'bjcp-2021',
    categoryNumber: 29,
    categoryLetter: 'A',
    name: 'Fruit Beer',
    categoryName: 'Fruit Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A beer of any declared base style with fruit added, where the fruit is meant to be recognizable and complementary rather than a novelty. The entrant names the base style and the fruit; judges weigh the balance between them, since the numeric targets belong to whichever base was declared.',
    commercialExamples: [],
    tags: ['fruit', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-29b',
    guideline: 'bjcp-2021',
    categoryNumber: 29,
    categoryLetter: 'B',
    name: 'Fruit and Spice Beer',
    categoryName: 'Fruit Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A fruit beer that also carries spice, herb or vegetable character, entered with the base style, the fruit and the additional ingredients all declared. Vitals track the declared base, so the judging turns on whether fruit and spice reinforce each other or fight.',
    commercialExamples: [],
    tags: ['fruit', 'spiced', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-29c',
    guideline: 'bjcp-2021',
    categoryNumber: 29,
    categoryLetter: 'C',
    name: 'Specialty Fruit Beer',
    categoryName: 'Fruit Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A fruit beer with an extra twist — alternative fermentables, smoke, wood or another specialty treatment — all of which the entrant declares alongside the base style. Because the base can be anything, there are no published vitals and judges score integration and drinkability instead.',
    commercialExamples: [],
    tags: ['fruit', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-29d',
    guideline: 'bjcp-2021',
    categoryNumber: 29,
    categoryLetter: 'D',
    name: 'Grape Ale',
    categoryName: 'Fruit Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'Beer made with grapes, must or juice, in the Italian tradition that sits somewhere between a farmhouse ale and a light wine. The entrant declares the base beer and the grape variety, and judges look for vinous character woven into the beer rather than a blend that reads as two drinks.',
    commercialExamples: [],
    tags: ['fruit', 'grape', 'italian', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 30 — Spiced Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-30a',
    guideline: 'bjcp-2021',
    categoryNumber: 30,
    categoryLetter: 'A',
    name: 'Spice, Herb, or Vegetable Beer',
    categoryName: 'Spiced Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'Any declared base style flavored with spices, herbs or vegetables, which the entrant must list so judges know what to look for. The additions should be identifiable and in proportion to the beer underneath; vitals come from the declared base rather than from this entry.',
    commercialExamples: [],
    tags: ['spiced', 'herb', 'vegetable', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-30b',
    guideline: 'bjcp-2021',
    categoryNumber: 30,
    categoryLetter: 'B',
    name: 'Autumn Seasonal Beer',
    categoryName: 'Spiced Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A spiced beer built around harvest flavors — pumpkin, squash, sweet potato and baking spice being the usual cast. The entrant declares the base style and the additions, and judges reward a beer that still drinks like beer rather than like liquid pie.',
    commercialExamples: [],
    tags: ['spiced', 'seasonal', 'autumn', 'pumpkin', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-30c',
    guideline: 'bjcp-2021',
    categoryNumber: 30,
    categoryLetter: 'C',
    name: 'Winter Seasonal Beer',
    categoryName: 'Spiced Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A darker, stronger holiday beer spiced in the direction of cinnamon, ginger, nutmeg and dried fruit. The base style and additions are declared by the entrant, so judging turns on warmth, festivity and balance instead of a published numeric target.',
    commercialExamples: ['Anchor Our Special Ale', 'Great Lakes Christmas Ale'],
    tags: ['spiced', 'seasonal', 'winter', 'strong', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-30d',
    guideline: 'bjcp-2021',
    categoryNumber: 30,
    categoryLetter: 'D',
    name: 'Specialty Spice Beer',
    categoryName: 'Spiced Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A spiced beer that also uses another specialty technique — smoke, wood, alternative sugars or similar — with every element declared on entry. There is no fixed envelope: the beer is measured against the base style the brewer names and against how well the layers work together.',
    commercialExamples: [],
    tags: ['spiced', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 31 — Alternative Fermentables
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-31a',
    guideline: 'bjcp-2021',
    categoryNumber: 31,
    categoryLetter: 'A',
    name: 'Alternative Grain Beer',
    categoryName: 'Alternative Fermentables',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A declared base style rebuilt with grains outside the usual barley-and-wheat vocabulary — rye, oats, corn, rice, sorghum, buckwheat and the like. The grain should change the beer audibly in flavor or texture, and vitals follow whatever base style the entrant names.',
    commercialExamples: [],
    tags: ['alternative-fermentables', 'grain', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-31b',
    guideline: 'bjcp-2021',
    categoryNumber: 31,
    categoryLetter: 'B',
    name: 'Alternative Sugar Beer',
    categoryName: 'Alternative Fermentables',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A declared base style using sugars beyond standard brewing sugar — honey, maple, molasses, agave, treacle or candi syrup. Judges look for the sugar to shape aroma, dryness or body in a recognizable way, and the numeric targets are those of the declared base.',
    commercialExamples: [],
    tags: ['alternative-fermentables', 'sugar', 'honey', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 32 — Smoked Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-32a',
    guideline: 'bjcp-2021',
    categoryNumber: 32,
    categoryLetter: 'A',
    name: 'Classic Style Smoked Beer',
    categoryName: 'Smoked Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'An existing classic style given a smoke character, with the base style and the wood or malt source declared on entry. The smoke has to sit in balance with the beer underneath, and the vitals judges use are simply those of the declared base style.',
    commercialExamples: [
      'Schlenkerla Rauchbier Marzen',
      'Spezial Rauchbier Lager',
      'Alaskan Smoked Porter',
    ],
    tags: ['smoked', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-32b',
    guideline: 'bjcp-2021',
    categoryNumber: 32,
    categoryLetter: 'B',
    name: 'Specialty Smoked Beer',
    categoryName: 'Smoked Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A smoked beer that additionally departs from its base style — extra fermentables, fruit, spice or wood — with all of it declared by the entrant. No published envelope exists, so judges assess whether the smoke and the other additions cohere into one beer.',
    commercialExamples: [],
    tags: ['smoked', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 33 — Wood Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-33a',
    guideline: 'bjcp-2021',
    categoryNumber: 33,
    categoryLetter: 'A',
    name: 'Wood-Aged Beer',
    categoryName: 'Wood Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A declared base style matured on wood, whether fresh oak or a barrel that previously held spirits or wine. Vanilla, tannin and any spirit character should support the beer rather than swamp it, and the numbers judges work to are those of the declared base style.',
    commercialExamples: [],
    tags: ['wood-aged', 'barrel-aged', 'oak', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-33b',
    guideline: 'bjcp-2021',
    categoryNumber: 33,
    categoryLetter: 'B',
    name: 'Specialty Wood-Aged Beer',
    categoryName: 'Wood Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A wood-aged beer carrying a further specialty element on top of the wood, such as fruit, spice or alternative sugars, all declared at entry. With no fixed vitals, judging comes down to how the wood, the addition and the declared base style balance in the glass.',
    commercialExamples: [],
    tags: ['wood-aged', 'barrel-aged', 'specialty', 'base-style-varies'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 34 — Specialty Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-34a',
    guideline: 'bjcp-2021',
    categoryNumber: 34,
    categoryLetter: 'A',
    name: 'Clone Beer',
    categoryName: 'Specialty Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A deliberate reproduction of a specific commercial beer, which the entrant names so judges know the target. There is no style envelope at all — the entry is scored on how closely it matches the original and on its quality as a beer in its own right.',
    commercialExamples: [],
    tags: ['specialty', 'clone', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-34b',
    guideline: 'bjcp-2021',
    categoryNumber: 34,
    categoryLetter: 'B',
    name: 'Mixed-Style Beer',
    categoryName: 'Specialty Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'A beer that deliberately combines two existing styles, both of which the entrant declares. Since no published vitals describe the hybrid, judges look for a coherent beer that shows recognizable traits of each parent instead of a muddle between them.',
    commercialExamples: [],
    tags: ['specialty', 'hybrid', 'base-style-varies'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-34c',
    guideline: 'bjcp-2021',
    categoryNumber: 34,
    categoryLetter: 'C',
    name: 'Experimental Beer',
    categoryName: 'Specialty Beer',
    vitals: {
      og: null,
      fg: null,
      ibu: null,
      srm: null,
      abv: null,
    },
    overallImpression:
      'The catch-all for beers that fit nowhere else, where the entrant explains the intent, the process and any unusual ingredients. Nothing is published to measure against, so the entry is judged on whether it achieves what the brewer set out to do and remains drinkable.',
    commercialExamples: [],
    tags: ['specialty', 'experimental', 'base-style-varies'],
    provenance: 'representative',
  },
];
